import { cloneDocumentMeta, createGroupLayer, createShapeLayer } from '../core/document';
import { createId } from '../core/ids';
import { applyLayerPatch, findLayerById, insertLayerAt, mapLayer } from '../core/layerTree';
import type { Mat3 } from '../core/math';
import type { DocumentMeta, RasterLayer, Rect, RGBA, ShapeLayer, ShapePaint } from '../core/types';
import { warpPoint, type WarpMesh } from '../core/warp';
import { getEngine } from '../engine/engine';
import {
  layerMaskOp,
  metaOp,
  selectionOp,
  surfaceOp,
  type EngineHistoryOp,
  type FullSnapshot,
} from '../engine/historyOps';
import {
  fillPathIntoLayer,
  pathCoverage,
  renderShapeLayer,
  renderVectorMask,
  shapeSubpaths,
} from '../engine/shapeOps';
import { deformSubpaths, transformSubpaths } from '../vector/geometry';
import { vectorizeImage, type TracedRegion, type VectorizeOptions } from '../vector/vectorize';
import { emptyPath, type ShapeGeometry, type ShapeStroke, type Subpath, type VectorPath } from '../vector/types';
import {
  activeDocument,
  commitHistory,
  commitMeta,
  insertAboveActive,
  syncSelectionFlag,
} from './actions';
import { useAppStore, type AppState } from './store';

const engine = getEngine();

function state(): AppState {
  return useAppStore.getState();
}

// --- Edit transactions ---

interface VectorEdit {
  docId: string;
  meta: DocumentMeta;
  /** Shape layer whose raster is rebuilt as the geometry changes, if any. */
  layerId: string | null;
  surface: FullSnapshot | null;
}

let pendingEdit: VectorEdit | null = null;

/**
 * Starts a drag that writes geometry through the transient setters. Both the
 * metadata and the shape's raster are snapshotted here, because the transient
 * writes have already overwritten them by the time the drag ends.
 */
export function beginVectorEdit(layerId: string | null): void {
  const doc = activeDocument();
  if (!doc) return;
  pendingEdit = {
    docId: doc.id,
    meta: cloneDocumentMeta(doc),
    layerId,
    surface: layerId ? engine.snapshotSurface(doc, layerId) : null,
  };
}

export function commitVectorEdit(label: string): void {
  const edit = pendingEdit;
  pendingEdit = null;
  if (!edit) return;
  const doc = activeDocument();
  if (!doc || doc.id !== edit.docId) return;

  const ops: EngineHistoryOp[] = [];
  if (edit.layerId) {
    ops.push(surfaceOp(edit.layerId, edit.surface, engine.snapshotSurface(doc, edit.layerId)));
  }
  ops.push(metaOp(edit.meta, cloneDocumentMeta(doc)));
  commitHistory(doc.id, label, ops);
}

export function cancelVectorEdit(): void {
  pendingEdit = null;
}

// --- Shape layers ---

function findShape(doc: DocumentMeta, layerId: string): ShapeLayer | null {
  const layer = findLayerById(doc.layers, layerId);
  return layer && layer.kind === 'shape' ? layer : null;
}

/**
 * Creates a shape layer above the active one and rasterises it. The geometry is
 * kept on the layer, so the shape can be re-drawn at any size later.
 */
export function addShapeLayer(
  geometry: ShapeGeometry,
  fill: ShapePaint,
  stroke: (ShapeStroke & { color: RGBA }) | null,
  name = 'Shape',
): string | null {
  const doc = activeDocument();
  if (!doc) return null;

  const layer = createShapeLayer(name, geometry, fill, stroke);
  const layers = insertLayerAt(doc.layers, insertAboveActive(doc), layer);
  const next: DocumentMeta = { ...doc, layers, activeLayerId: layer.id };

  state().setDocument(next);
  renderShapeLayer(engine, next, layer, false);
  commitMeta(doc, 'New Shape Layer', next, [
    surfaceOp(layer.id, null, engine.snapshotSurface(next, layer.id)),
  ]);
  return layer.id;
}

/** Live geometry update while a shape is being dragged; nothing reaches history. */
export function updateShapeGeometryTransient(layerId: string, geometry: ShapeGeometry): void {
  const doc = activeDocument();
  if (!doc) return;
  const shape = findShape(doc, layerId);
  if (!shape) return;

  const updated: ShapeLayer = { ...shape, geometry };
  state().replaceDocument(doc.id, (current) => ({
    ...current,
    layers: mapLayer(current.layers, layerId, () => updated),
  }));
  renderShapeLayer(engine, doc, updated, false);
  engine.invalidate();
}

export function updateShapeGeometry(
  layerId: string,
  geometry: ShapeGeometry,
  label = 'Edit Shape',
): void {
  const doc = activeDocument();
  if (!doc) return;
  const shape = findShape(doc, layerId);
  if (!shape) return;

  const updated: ShapeLayer = { ...shape, geometry };
  const ops = renderShapeLayer(engine, doc, updated);
  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layerId, () => updated),
  };
  commitMeta(doc, label, next, ops);
}

export function setShapeFill(layerId: string, fill: ShapePaint): void {
  const doc = activeDocument();
  if (!doc) return;
  const shape = findShape(doc, layerId);
  if (!shape) return;

  const updated: ShapeLayer = { ...shape, fill };
  const ops = renderShapeLayer(engine, doc, updated);
  commitMeta(doc, 'Shape Fill', { ...doc, layers: mapLayer(doc.layers, layerId, () => updated) }, ops);
}

export function setShapeStroke(
  layerId: string,
  stroke: (ShapeStroke & { color: RGBA }) | null,
): void {
  const doc = activeDocument();
  if (!doc) return;
  const shape = findShape(doc, layerId);
  if (!shape) return;

  const updated: ShapeLayer = { ...shape, stroke };
  const ops = renderShapeLayer(engine, doc, updated);
  commitMeta(
    doc,
    'Shape Stroke',
    { ...doc, layers: mapLayer(doc.layers, layerId, () => updated) },
    ops,
  );
}

/**
 * Moves or warps a shape layer by transforming its geometry and drawing it
 * again, rather than resampling the pixels it happens to have now. Returns false
 * when the layer is not a shape, so the caller can fall back to a pixel warp.
 */
export function transformShapeLayer(layerId: string, matrix: Mat3, label = 'Transform Shape'): boolean {
  const doc = activeDocument();
  if (!doc) return false;
  const shape = findShape(doc, layerId);
  if (!shape) return false;

  const subpaths = transformSubpaths(shapeSubpaths(shape), matrix);
  updateShapeGeometry(layerId, { kind: 'freeform', subpaths }, label);
  return true;
}

/**
 * Warp for a shape layer. The mesh bends the geometry rather than the pixels, so
 * the shape stays resolution independent and can be warped again later.
 */
export function warpShapeLayer(
  layerId: string,
  frame: Rect,
  mesh: WarpMesh,
  label = 'Warp Shape',
): boolean {
  const doc = activeDocument();
  if (!doc) return false;
  const shape = findShape(doc, layerId);
  if (!shape) return false;

  const subpaths = deformSubpaths(shapeSubpaths(shape), (point) => {
    const s = frame.width > 0 ? (point.x - frame.x) / frame.width : 0;
    const t = frame.height > 0 ? (point.y - frame.y) / frame.height : 0;
    return warpPoint(mesh, s, t);
  });
  updateShapeGeometry(layerId, { kind: 'freeform', subpaths }, label);
  return true;
}

function toRasterLayer(shape: ShapeLayer): RasterLayer {
  return {
    kind: 'raster',
    id: shape.id,
    name: shape.name,
    visible: shape.visible,
    opacity: shape.opacity,
    blendMode: shape.blendMode,
    blendChannels: shape.blendChannels,
    blendIf: shape.blendIf,
    locked: shape.locked,
    lockPixels: shape.lockPixels,
    lockPosition: shape.lockPosition,
    lockMask: shape.lockMask,
    hasMask: shape.hasMask,
    maskEnabled: shape.maskEnabled,
    maskLinked: shape.maskLinked,
    clipping: shape.clipping,
    vectorMask: shape.vectorMask,
    effects: shape.effects,
    contentOffset: shape.contentOffset ?? { x: 0, y: 0 },
  };
}

/**
 * Layer - Rasterize: the pixels are already there, so this only forgets the
 * geometry by turning the layer into a plain raster one.
 */
export function rasterizeShapeLayer(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const shape = findShape(doc, layerId);
  if (!shape) return;

  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layerId, (layer) =>
      layer.kind === 'shape' ? toRasterLayer(layer) : layer,
    ),
  };
  commitMeta(doc, 'Rasterize Shape', next);
}

// --- Vectorize Bitmap ---

export interface VectorizeSettings extends VectorizeOptions {
  colors: number;
}

/** Regions traced from the active layer, for the dialog preview. */
export function traceActiveLayer(settings: VectorizeSettings): TracedRegion[] {
  const doc = activeDocument();
  const layerId = doc?.activeLayerId;
  if (!doc || !layerId) return [];
  const surface = engine.snapshotSurface(doc, layerId);
  if (!surface.data) return [];
  return vectorizeImage(surface.data, surface.width, surface.height, settings);
}

/**
 * Turns the active layer into a group of shape layers, one per traced colour.
 * The largest region sits at the bottom so smaller details stay visible.
 */
export function vectorizeActiveLayer(settings: VectorizeSettings): void {
  const doc = activeDocument();
  const layerId = doc?.activeLayerId;
  if (!doc || !layerId) return;

  const regions = traceActiveLayer(settings);
  if (regions.length === 0) {
    state().setStatus('Nothing to vectorize on this layer');
    return;
  }

  const shapes = regions.map((region, index) =>
    createShapeLayer(
      `Region ${index + 1}`,
      { kind: 'freeform', subpaths: region.subpaths },
      { kind: 'solid', color: region.color },
      null,
    ),
  );
  const group = createGroupLayer('Vectorized', shapes);
  const layers = insertLayerAt(doc.layers, insertAboveActive(doc), group);
  const next: DocumentMeta = { ...doc, layers, activeLayerId: group.id };

  state().setDocument(next);
  const ops: EngineHistoryOp[] = [];
  for (const shape of shapes) {
    renderShapeLayer(engine, next, shape, false);
    ops.push(surfaceOp(shape.id, null, engine.snapshotSurface(next, shape.id)));
  }
  commitMeta(doc, 'Vectorize Bitmap', next, ops);
  state().setStatus(`Vectorized into ${shapes.length} shapes`);
}

// --- Paths panel ---

function withPaths(doc: DocumentMeta, paths: VectorPath[], activePathId = doc.activePathId): DocumentMeta {
  return { ...doc, paths, activePathId };
}

export function activePath(doc: DocumentMeta): VectorPath | null {
  return doc.paths.find((path) => path.id === doc.activePathId) ?? null;
}

/** Name Photoshop gives the scratch path the Pen tool draws into. */
export const WORK_PATH_NAME = 'Work Path';

function nextPathName(paths: readonly VectorPath[]): string {
  for (let i = 1; ; i += 1) {
    const name = `Path ${i}`;
    if (!paths.some((path) => path.name === name)) return name;
  }
}

/**
 * The path the Pen tool writes into. Drawing with no path selected starts a Work
 * Path, exactly as Photoshop does; saving it is what turns it into a real one.
 */
export function ensureWorkPath(): VectorPath | null {
  const doc = activeDocument();
  if (!doc) return null;
  const existing = activePath(doc);
  if (existing) return existing;

  const work = doc.paths.find((path) => path.name === WORK_PATH_NAME);
  if (work) {
    state().setDocument({ ...doc, activePathId: work.id });
    return work;
  }

  const path = emptyPath(createId('path'), WORK_PATH_NAME);
  commitMeta(doc, 'New Path', withPaths(doc, [...doc.paths, path], path.id));
  return path;
}

export function setActivePath(pathId: string | null): void {
  const doc = activeDocument();
  if (!doc || doc.activePathId === pathId) return;
  state().replaceDocument(doc.id, (current) => ({ ...current, activePathId: pathId }));
  engine.invalidate();
}

export function addPath(subpaths: Subpath[] = []): string | null {
  const doc = activeDocument();
  if (!doc) return null;
  const path: VectorPath = { id: createId('path'), name: nextPathName(doc.paths), subpaths };
  commitMeta(doc, 'New Path', withPaths(doc, [...doc.paths, path], path.id));
  return path.id;
}

export function deletePath(pathId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const paths = doc.paths.filter((path) => path.id !== pathId);
  const activeId = doc.activePathId === pathId ? null : doc.activePathId;
  commitMeta(doc, 'Delete Path', withPaths(doc, paths, activeId));
}

export function duplicatePath(pathId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const source = doc.paths.find((path) => path.id === pathId);
  if (!source) return;
  const copy: VectorPath = {
    id: createId('path'),
    name: nextPathName(doc.paths),
    subpaths: source.subpaths.map((subpath) => ({ ...subpath, knots: subpath.knots.map((k) => ({ ...k })) })),
  };
  commitMeta(doc, 'Duplicate Path', withPaths(doc, [...doc.paths, copy], copy.id));
}

export function renamePath(pathId: string, name: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const trimmed = name.trim();
  if (!trimmed) return;
  const paths = doc.paths.map((path) => (path.id === pathId ? { ...path, name: trimmed } : path));
  commitMeta(doc, 'Rename Path', withPaths(doc, paths));
}

/** Live path edit while the Pen or a selection tool is dragging. */
export function updatePathTransient(pathId: string, subpaths: Subpath[]): void {
  const doc = activeDocument();
  if (!doc) return;
  state().replaceDocument(doc.id, (current) => ({
    ...current,
    paths: current.paths.map((path) => (path.id === pathId ? { ...path, subpaths } : path)),
  }));
  engine.invalidate();
}

export function updatePath(pathId: string, subpaths: Subpath[], label = 'Edit Path'): void {
  const doc = activeDocument();
  if (!doc) return;
  const paths = doc.paths.map((path) => (path.id === pathId ? { ...path, subpaths } : path));
  commitMeta(doc, label, withPaths(doc, paths));
}

/** Paths panel: turns the scratch Work Path into a saved one. */
export function saveWorkPath(): void {
  const doc = activeDocument();
  if (!doc) return;
  const work = doc.paths.find((path) => path.name === WORK_PATH_NAME);
  if (!work) return;
  const paths = doc.paths.map((path) =>
    path.id === work.id ? { ...path, name: nextPathName(doc.paths) } : path,
  );
  commitMeta(doc, 'Save Path', withPaths(doc, paths));
}

export function pathToSelection(pathId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const path = doc.paths.find((item) => item.id === pathId);
  if (!path) return;
  const coverage = pathCoverage(doc, path.subpaths);
  if (!coverage) return;

  const resources = engine.resourcesFor(doc);
  const before = engine.snapshotSelection(doc);
  resources.selection.restore(coverage);
  resources.selection.refresh();
  syncSelectionFlag(doc.id);
  commitHistory(doc.id, 'Path to Selection', [
    selectionOp(before, engine.snapshotSelection(doc)),
  ]);
}

/** Paths panel: Fill Path and Stroke Path, both painting into the active layer. */
export function fillPath(pathId: string, stroke = false): void {
  const doc = activeDocument();
  if (!doc) return;
  const path = doc.paths.find((item) => item.id === pathId);
  const layerId = doc.activeLayerId;
  if (!path || !layerId) return;

  const ops = fillPathIntoLayer(engine, doc, layerId, path.subpaths, {
    color: state().foreground,
    opacity: 1,
    stroke: stroke
      ? {
          width: Math.max(1, state().paintOptions.brush.size / 4),
          align: 'center',
          join: 'round',
          cap: 'round',
          dash: [],
          color: state().foreground,
        }
      : null,
  });
  commitHistory(doc.id, stroke ? 'Stroke Path' : 'Fill Path', ops);
}

/**
 * Adds subpaths to a shape layer that is already selected, which is how a path
 * operation other than Combine is meant to work: it cuts into the shape you are
 * on instead of starting a new one.
 */
export function appendToShapeLayer(layerId: string, subpaths: Subpath[]): boolean {
  const doc = activeDocument();
  if (!doc) return false;
  const shape = findShape(doc, layerId);
  if (!shape) return false;

  updateShapeGeometry(
    layerId,
    { kind: 'freeform', subpaths: [...shapeSubpaths(shape), ...subpaths] },
    'Combine Shapes',
  );
  return true;
}

/** Pen and shape tools in Path mode append into the path being edited. */
export function appendSubpathToActivePath(subpath: Subpath): void {
  const work = ensureWorkPath();
  const doc = activeDocument();
  if (!work || !doc) return;
  const path = doc.paths.find((item) => item.id === work.id) ?? work;
  updatePath(path.id, [...path.subpaths, subpath], 'Add Subpath');
}

/** Pen and shape tools in Pixels mode paint straight into the active layer. */
export function paintSubpathsIntoLayer(
  subpaths: Subpath[],
  stroke: (ShapeStroke & { color: RGBA }) | null = null,
): void {
  const doc = activeDocument();
  const layerId = doc?.activeLayerId;
  if (!doc || !layerId) return;
  const ops = fillPathIntoLayer(engine, doc, layerId, subpaths, {
    color: state().foreground,
    opacity: 1,
    stroke,
  });
  commitHistory(doc.id, stroke ? 'Stroke Path' : 'Fill Path', ops);
}

// --- Vector masks ---

/**
 * Layer - Vector Mask - Add. The path is stored on the layer and rasterised into
 * the same texture a raster mask uses, so nothing downstream changes.
 */
export function addVectorMask(layerId: string, subpaths: Subpath[]): void {
  const doc = activeDocument();
  if (!doc) return;
  const layer = findLayerById(doc.layers, layerId);
  if (!layer) return;

  const ops: EngineHistoryOp[] = layer.hasMask
    ? renderVectorMask(engine, doc, layerId, subpaths)
    : (() => {
        renderVectorMask(engine, doc, layerId, subpaths, false);
        return [layerMaskOp(layerId, null, engine.snapshotLayerMask(doc, layerId))];
      })();

  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layerId, (target) =>
      applyLayerPatch(target, { hasMask: true, maskEnabled: true, vectorMask: subpaths }),
    ),
  };
  commitMeta(doc, 'Add Vector Mask', next, ops);
}

export function updateVectorMask(layerId: string, subpaths: Subpath[]): void {
  const doc = activeDocument();
  if (!doc) return;
  const ops = renderVectorMask(engine, doc, layerId, subpaths);
  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layerId, (target) =>
      applyLayerPatch(target, { vectorMask: subpaths }),
    ),
  };
  commitMeta(doc, 'Edit Vector Mask', next, ops);
}

export function removeVectorMask(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const before = engine.snapshotLayerMask(doc, layerId);
  engine.peek(doc.id)?.removeMask(layerId);
  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layerId, (layer) =>
      applyLayerPatch(layer, { hasMask: false, vectorMask: null }),
    ),
  };
  commitMeta(doc, 'Delete Vector Mask', next, [layerMaskOp(layerId, before, null)]);
}

/** Layer - Vector Mask - From Shape, and the same for the active path. */
export function vectorMaskFromPath(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const path = activePath(doc);
  if (!path || path.subpaths.length === 0) {
    state().setStatus('Draw a path first');
    return;
  }
  addVectorMask(layerId, path.subpaths);
}

/** Paths panel: makes a path out of a shape layer's geometry. */
export function pathFromShapeLayer(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const shape = findShape(doc, layerId);
  if (!shape) return;
  addPath(shapeSubpaths(shape));
}